import { useRef, useState, useCallback } from "react";
import { MoveHorizontal, ImageOff } from "lucide-react";

const FallbackPane = ({ label }) => (
  <div className="absolute inset-0 flex items-center justify-center bg-brand-mist/60">
    <span className="flex flex-col items-center gap-2 text-brand-muted">
      <ImageOff size={26} aria-hidden="true" />
      <span className="text-xs font-semibold tracking-wide uppercase">{label} image unavailable</span>
    </span>
  </div>
);

export const BeforeAfterSlider = ({ beforeImage, afterImage, altBefore, altAfter, testId }) => {
  const [pos, setPos] = useState(50);
  const [err, setErr] = useState({ before: false, after: false });
  const ref = useRef(null);
  const dragging = useRef(false);

  const update = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(96, Math.max(4, pct)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    update(e.clientX);
  };
  const onPointerMove = (e) => {
    if (dragging.current) update(e.clientX);
  };
  const stop = () => { dragging.current = false; };

  return (
    <div
      ref={ref}
      data-testid={testId}
      className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-brand-line/60 shadow-[0_4px_40px_rgba(0,0,0,0.05)] select-none cursor-ew-resize touch-none bg-brand-mist/40"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stop}
      onPointerLeave={stop}
      role="slider"
      aria-label="Before and after comparison slider"
      aria-valuenow={Math.round(pos)}
      aria-valuemin={0}
      aria-valuemax={100}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") setPos((p) => Math.max(4, p - 5));
        if (e.key === "ArrowRight") setPos((p) => Math.min(96, p + 5));
      }}
    >
      {err.after ? (
        <FallbackPane label="After" />
      ) : (
        <img
          src={afterImage}
          alt={altAfter}
          loading="lazy"
          draggable={false}
          onError={() => setErr((p) => ({ ...p, after: true }))}
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}

      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        {err.before ? (
          <FallbackPane label="Before" />
        ) : (
          <img
            src={beforeImage}
            alt={altBefore}
            loading="lazy"
            draggable={false}
            onError={() => setErr((p) => ({ ...p, before: true }))}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
      </div>

      <span className="absolute top-3 left-3 bg-brand-ink/70 backdrop-blur text-white text-[10px] font-bold tracking-[0.16em] uppercase px-3 py-1.5 rounded-full pointer-events-none">
        Before
      </span>
      <span className="absolute top-3 right-3 bg-brand text-white text-[10px] font-bold tracking-[0.16em] uppercase px-3 py-1.5 rounded-full pointer-events-none">
        After
      </span>
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_12px_rgba(0,0,0,0.4)] pointer-events-none"
        style={{ left: `${pos}%` }}
      >
        <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center text-brand">
          <MoveHorizontal size={18} />
        </span>
      </div>
    </div>
  );
};
